import * as THREE from 'three';
import { Object3D, Box3, Vector3 } from 'three';
import { type OrbitControls as OrbitControlsImpl } from 'three-stdlib';

import { AdaptiveDpr, AdaptiveEvents, OrbitControls, Stats } from '@react-three/drei';
import { type ThreeEvent, useFrame, useThree } from '@react-three/fiber';
import {
  memo,
  Suspense,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  forwardRef,
} from 'react';

import { v4 as uuid } from 'uuid';

import type {
  Matrix4,
  PointPickResult,
  PolygonProps,
  RequiredId,
  SimplePointCloudProps,
  SphericalImageProps,
  SphericalImageSetFromUrlProps,
  SphericalImageSetProps,
  SphericalImageViewerClickEvent,
  CameraTargetProps,
  AnimationProps,
  AbyssViewerCallbacks,
  AnimationStepProps,
  OctreeProgress,
  ImageSetProps,
  CurrentImageProps,
  Number3,
  ModelProps,
  ViewerEvent,
  SvgMarkerSetProps,
  HtmlMarkerSetProps,
  ImageProps,
} from '../types';

import { UnitSystemEnum, ImageType, ViewerFileType } from './Enums';
import { startAnimation as startAnimationImpl } from './Animation/Animation';
import { AnimationPlayer } from './Animation/AnimationPlayer';
import { BrushToolHelper, type BrushToolProps } from './BrushToolHelper';
import { CameraMover } from './CameraMover';
import { DraggingHelper } from './DraggingHelper';
import { type FileLoaderOptions, FileLoaderOptionsContext } from './FileLoaderOptionsContext';
import { FloorLevel, type FloorLevelProps } from './FloorLevel';
import { Marker, type MarkerProps } from './Marker';
import { Model } from './Model';
import {
  NavigationControls,
  type NavigationControlsProps,
  NavigationControlType,
} from './Navigation/NavigationControls';
import { OctreePointCloudMaster, type OctreePointCloudProps } from './Octree/OctreePointCloud';
import { defaultPickCanvasAreaPercent } from './PickHelper';
import {
  calcScale,
  makeVisibilityBoxUniforms,
  PointCloudMaterial,
  type PointCloudMaterialProps,
} from './PointCloudMaterial';
import type { PointCoordinates } from './PointCoordinates';
import { defaultPointLights, type PointLightProps } from './PointLight';
import { Polygon } from './Polygon';
import { ScreenShotHelper, type ScreenShotHelperHandlers } from './ScreenShotHelper';
import { Segment, type SegmentProps } from './Segment';
import { type SelectBox, useSelectBoxControls } from './SelectBoxControls';
import { SimplePointCloud } from './SimplePointCloud';
import { InstancedImageSet } from './Image/InstancedImageSet';
import { InstancedSphericalImageSet } from './SphericalImage';
import { SphericalImageSetFromUrl } from './SphericalImage/SphericalImageSetFromUrl';
import { SphericalImageViewer } from './SphericalImage/SphericalImageViewer';
import { ImageViewer } from './Image/ImageViewer';
import { SvgMarker, type SvgMarkerProps } from './SvgMarker';
import {
  alignModelByTwoVectors,
  fovFromCalibration,
  cameraQuaternionFromAbyssRotation,
  getObjectBoundingBox,
  getTransformTargetBoundingBox,
} from './Util';
import { defaultViewCubeProps, ViewCube, type ViewCubeProps } from './ViewCube';
import { ViewerLayer } from './ViewerLayer';
import {
  defaultVisibilityBoxStyle,
  type RotatedVisibilityBoxProps,
  type Transform,
  VisibilityBoxMode,
} from './VisibilityBox';
import { VisibilityBoxControl } from './VisibilityBoxControl';
import { MeasurementLines, type MeasurementLinesProps } from './MeasurementLines';
import { useClickHandlers } from './useClickHandlers';
import { useFrameTimings } from './useFrameTimings';
import {
  AbyssViewerContextProvider,
  type CameraProps,
  type CanvasProps,
  useAbyssViewerContext,
} from './AbyssViewerContext';
import { useEdlEffect, useEdlRenderer } from './EyeDomeLighting/useEdlRenderer';
import { useSharedGeometries } from './useSharedGeometries';
import {
  CALLBACK_ORDER_AFTER_CAMERA_UPDATE,
  CALLBACK_ORDER_CAMERA_MOVER,
} from './FrameCallbackOrder';
import { SvgMarkerSet } from './SvgMarkerSet';
import { HtmlMarkerSet } from './HtmlMarkerSet';
import { useHybridRaycaster } from './useHybridRaycaster';
import { LoadErrorBoundary } from './LoadErrorBoundary';
import { useSetTimeout } from '../helpers/hooks';
import { getBoundingBoxOfRotatedVBox, useVisibilityBox } from './useVisibilityBox';

export interface AbyssViewerComponentProps {
  onSceneLoaded?: () => void;
  onInitialPointCloudLoad?: () => void;
  cameraTarget?: CameraTargetProps;
  navigationControls?: NavigationControlsProps;
  visibilityBox?: RotatedVisibilityBoxProps;
  setVisibilityBox?: (newBox: RotatedVisibilityBoxProps) => void;
  smallVisibilityBox?: RotatedVisibilityBoxProps;
  setSmallVisibilityBox?: (newBox: RotatedVisibilityBoxProps) => void;
  brushTool?: BrushToolProps;
  models?: ModelProps[];
  markers?: Omit<MarkerProps, 'onMarkerClick'>[];
  svgMarkers?: SvgMarkerProps[];
  svgMarkerSets?: SvgMarkerSetProps[];
  htmlMarkerSets?: HtmlMarkerSetProps[];
  segments?: SegmentProps[];
  pointCloudMaterials?: Map<string, PointCloudMaterialProps>;
  simplePointClouds?: SimplePointCloudProps[];
  pointClouds?: OctreePointCloudProps[];
  pointBudget?: number;
  invisiblePointsBudgetRatio?: number;
  enableAdaptivePointBudget?: boolean;
  enableLoaderRetry?: boolean;
  enableEDL?: boolean;
  edlStrength?: number;
  showPointCloudsInSphericalView?: boolean;
  sphericalImageSets?: SphericalImageSetProps[];
  sphericalImageSetsFromUrl?: SphericalImageSetFromUrlProps[];
  imageSets?: ImageSetProps[];
  showMaxNeighbouringImages?: number;
  maxNeighbouringImageAngle?: number;
  viewCube?: ViewCubeProps | boolean;
  floorLevels?: FloorLevelProps[];
  measurementLines?: MeasurementLinesProps;
  northRotation?: number;
  selectionColors?: { shiftKeyColor: number; altKeyColor: number };
  sphericalViewerImageOpacity?: number;
  imageBrightness?: number;
  imageContrast?: number;
  sphereRadius?: number;
  sphericalImageVisibilityRange?: Number3;
  pointLights?: PointLightProps[];
  ambientLightIntensity?: number;
  fileLoaderOptions?: { withCredentials: boolean };
  polygons?: PolygonProps[];
  onClick?: (event: ViewerEvent<PointerEvent>) => void;
  onAnnotationClick?: (annotationId: string) => void;
  onMeshClick?: (point: PointCoordinates, event: ThreeEvent<MouseEvent>) => void;
  onPointCloudClick?: (pointPickResult: PointPickResult) => void;
  onPointCloudSelection?: (pointPickResults: PointPickResult[]) => void;
  onPointCloudHover?: (pointPickResult: PointPickResult | undefined) => void;
  onSphericalViewClick?: (event: SphericalImageViewerClickEvent) => void;
  currentImage?: CurrentImageProps;
  setCurrentImage?: (image: CurrentImageProps | undefined) => void;
  nextCurrentImage?: CurrentImageProps;
  setNextCurrentImage?: (image: CurrentImageProps | undefined) => void;
  shouldHandleSphericalViewCameraTransitions?: boolean;
  onOctreeProgress?: (progress: OctreeProgress) => void;
  pickCanvasAreaPercent?: number;
  selectionBoxIsEnabled?: boolean;
  unitSystem?: UnitSystemEnum;
  onFileLoadError?: (
    fileType: ViewerFileType,
    url: string,
    error: Error,
    info: React.ErrorInfo
  ) => void;
}

export interface AbyssViewerProps {
  abyssViewerComponentProps?: AbyssViewerComponentProps;
  canvasProps: CanvasProps;
  onMajorPerformanceCaveat?: () => void;
  cameraProps?: CameraProps;
  showStats?: boolean;
  displayAnimationEditor?: boolean;
  initialAnimationSteps?: AnimationStepProps[];
  children?: React.ReactNode;
  overlayNode?: React.ReactNode;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface AbyssViewerNoContextProps
  extends Omit<
    AbyssViewerProps,
    | 'canvasProps'
    | 'cameraProps'
    | 'onMajorPerformanceCaveat'
    | 'displayAnimationEditor'
    | 'initialAnimationSteps'
  > {}

interface AbyssViewerInnerProps
  extends Omit<AbyssViewerNoContextProps, 'showStats' | 'overlayNode'> {
  screenShotHelperReference: React.RefObject<ScreenShotHelperHandlers>;
}

const defaulOrbitRotationSpeed = 0.5;
const defaultAmbientLightIntensity = 0.6;

const defaultCameraFieldOfView = 50;
const defaultTargetDistance = 8;

const idMap = new WeakMap<object, string>();

const getCreateObjectId = (object: object, id?: string) => {
  if (id) {
    return id;
  }
  const existingId = idMap.get(object);
  if (existingId) {
    return existingId;
  }
  const newId = uuid();
  idMap.set(object, newId);
  return newId;
};

function makePropsWithId<T extends { id?: string }>(objectProps: T): T & RequiredId {
  const { id } = objectProps;
  return { ...objectProps, id: getCreateObjectId(objectProps, id) };
}

const MemoizedOrbitControls = memo(OrbitControls);

// const profilerCallback: React.ComponentProps<typeof Profiler>['onRender'] = (
//   id,
//   phase,
//   actualDuration,
//   baseDuration,
//   startTime,
//   commitTime,
//   interactions
// ) => {};

// function useTraceRender(props: any) {
//   const previous = useRef(props);
//   useEffect(() => {
//     const changedProps = Object.entries(props).reduce((ps, [k, v]) => {
//       if (previous.current[k] !== v) {
//         // eslint-disable-next-line no-param-reassign
//         ps[k] = [previous.current[k], v];
//       }
//       return ps;
//     }, {});
//     if (Object.keys(changedProps).length > 0) {
//       console.log('Changed props:', changedProps);
//     }
//     previous.current = props;
//   });
// }

const AbyssViewerInner = memo(
  forwardRef<AbyssViewerCallbacks, AbyssViewerInnerProps>(
    (
      {
        abyssViewerComponentProps: {
          onSceneLoaded,
          onInitialPointCloudLoad,
          onClick,
          onAnnotationClick,
          onMeshClick,
          onPointCloudClick,
          onPointCloudSelection,
          onPointCloudHover,
          onSphericalViewClick,
          cameraTarget,
          navigationControls: navigationControlsProps,
          smallVisibilityBox: smallVisibilityBoxOnParent,
          setSmallVisibilityBox: setSmallVisibilityBoxOnParent,
          visibilityBox: visibilityBoxOnParent,
          setVisibilityBox: setVisibilityBoxOnParent,
          brushTool: brushToolProps,
          segments,
          markers,
          svgMarkers,
          svgMarkerSets,
          htmlMarkerSets,
          models,
          simplePointClouds,
          pointClouds,
          pointCloudMaterials,
          showPointCloudsInSphericalView,
          sphericalImageSets,
          sphericalImageSetsFromUrl,
          imageSets,
          showMaxNeighbouringImages,
          maxNeighbouringImageAngle,
          viewCube,
          floorLevels,
          measurementLines,
          northRotation,
          selectionColors,
          sphericalViewerImageOpacity,
          imageBrightness = 0,
          imageContrast = 0,
          sphereRadius,
          sphericalImageVisibilityRange,
          pointLights = defaultPointLights,
          ambientLightIntensity = defaultAmbientLightIntensity,
          fileLoaderOptions,
          polygons,
          currentImage: currentImageOnParent,
          setCurrentImage: setCurrentImageOnParent,
          nextCurrentImage: nextCurrentImageOnParent,
          setNextCurrentImage: setNextCurrentImageOnParent,
          shouldHandleSphericalViewCameraTransitions,
          onOctreeProgress,
          pointBudget,
          invisiblePointsBudgetRatio,
          enableAdaptivePointBudget = true,
          enableLoaderRetry = false,
          enableEDL = true,
          edlStrength = 1,
          pickCanvasAreaPercent,
          selectionBoxIsEnabled = true,
          unitSystem = UnitSystemEnum.METRIC,
          onFileLoadError,
        } = {},
        screenShotHelperReference,
        children,
      },
      selfReference
    ) => {
      const {
        pickHelper,
        clippingPlanes,
        navigationControls,
        setNavigationControls,
        setDraggingHelper,
        getTransformTarget,
        setUnitSystem,
      } = useAbyssViewerContext();

      const { setTimeoutManaged } = useSetTimeout();

      //const raycaster = useThree((state) => state.raycaster);
      const camera = useThree((state) => state.camera);
      const gl = useThree((state) => state.gl);
      const scene = useThree((state) => state.scene);

      const { domElement } = gl;

      const [allObjectsGroup, setAllObjectsGroup] = useState<THREE.Group>();

      const markersGroup = useRef<THREE.Group | null>(null);
      const modelsGroup = useRef<THREE.Group | null>(null);
      const simplePointCloudsGroup = useRef<THREE.Group | null>(null);

      const sphericalImageSetsGroup = useRef<THREE.Group | null>(null);
      const imageSetsGroup = useRef<THREE.Group | null>(null);

      const handleAllObjectsGroupReference = useCallback((group: THREE.Group) => {
        if (group) {
          setAllObjectsGroup(group);
        } else {
          setAllObjectsGroup(undefined);
        }
      }, []);

      useEffect(() => {
        setUnitSystem(unitSystem);
      }, [setUnitSystem, unitSystem]);

      const [localCurrentImage, setLocalCurrentImage] = useState<CurrentImageProps | undefined>(
        undefined
      );
      const [localNextCurrentImage, setLocalNextCurrentImage] = useState<
        CurrentImageProps | undefined
      >(undefined);

      const currentImage = setCurrentImageOnParent ? currentImageOnParent : localCurrentImage;
      const nextCurrentImage = setNextCurrentImageOnParent
        ? nextCurrentImageOnParent
        : localNextCurrentImage;
      const setCurrentImage = setCurrentImageOnParent ?? setLocalCurrentImage;
      const setNextCurrentImage = setNextCurrentImageOnParent ?? setLocalNextCurrentImage;

      const isSphericalMode = !!nextCurrentImage || !!currentImage;

      const [cameraMover, setCameraMover] = useState<CameraMover>();

      useEffect(() => {
        return () => {
          navigationControls?.dispose();
        };
      }, [navigationControls]);

      useFrame(({ clock }) => {
        if (cameraMover) {
          cameraMover.update(clock.elapsedTime);
        }
      }, CALLBACK_ORDER_CAMERA_MOVER);

      const sharedGeometries = useSharedGeometries();

      const invalidate = useThree((state) => state.invalidate);
      const regress = useThree((state) => state.performance.regress);

      const frameTimings = useFrameTimings();

      useEffect(() => {
        if (!navigationControls) return;
        if (!camera) return;
        if (currentImage) {
          // enable Spherical View Mode
          navigationControls.setControlType(NavigationControlType.LookAround);
          camera.layers.disable(ViewerLayer.Default);
          camera.layers.enable(ViewerLayer.SphericalViewerLayer);
        } else {
          // disable Spherical View Mode
          navigationControls.setControlType(NavigationControlType.Orbit);
          camera.layers.enable(ViewerLayer.Default);
          camera.layers.disable(ViewerLayer.SphericalViewerLayer);
        }
      }, [camera, navigationControls, currentImage]);

      const previousCameraTarget = useRef<CameraTargetProps | undefined>(undefined);

      useEffect(() => {
        if (!navigationControls) return;
        if (!cameraMover) return;
        if (cameraTarget === previousCameraTarget.current) return;

        previousCameraTarget.current = cameraTarget;
        if (!cameraTarget) return;

        if (isSphericalMode) {
          // preserve camera position
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { position, ...cameraTargetWithoutPosition } = cameraTarget;
          cameraMover.moveCameraToTargetProps(cameraTargetWithoutPosition);
        } else {
          cameraMover.moveCameraToTargetProps(cameraTarget);
        }
      }, [cameraTarget, isSphericalMode, navigationControls, cameraMover]);

      const enableControls = useCallback(
        (isEnabled: boolean) => {
          if (navigationControls) {
            navigationControls.setIsEnabled(isEnabled);
          }
        },
        [navigationControls]
      );

      const lookAroundControls = navigationControls?.lookAroundControls;
      const lookAroundHSpeed = navigationControlsProps?.lookAround?.hspeed;
      const lookAroundVSpeed = navigationControlsProps?.lookAround?.vspeed;
      const lookAroundMaxFov = navigationControlsProps?.lookAround?.maxFov;
      const lookAroundMinFov = navigationControlsProps?.lookAround?.minFov;
      const lookAroundZoomStep = navigationControlsProps?.lookAround?.zoomStep;
      const orbitRotationSpeed = navigationControlsProps?.orbit?.rotationSpeed;

      useEffect(() => {
        if (lookAroundControls && lookAroundMaxFov !== undefined) {
          lookAroundControls.maxFov = lookAroundMaxFov;
        }
      }, [lookAroundControls, lookAroundMaxFov]);

      useEffect(() => {
        if (lookAroundControls && lookAroundMinFov !== undefined) {
          lookAroundControls.minFov = lookAroundMinFov;
        }
      }, [lookAroundControls, lookAroundMinFov]);

      useEffect(() => {
        if (lookAroundControls && lookAroundHSpeed !== undefined) {
          lookAroundControls.hspeed = lookAroundHSpeed;
        }
      }, [lookAroundControls, lookAroundHSpeed]);
      useEffect(() => {
        if (lookAroundControls && lookAroundVSpeed !== undefined) {
          lookAroundControls.vspeed = lookAroundVSpeed;
        }
      }, [lookAroundControls, lookAroundVSpeed]);
      useEffect(() => {
        if (lookAroundControls && lookAroundZoomStep !== undefined) {
          lookAroundControls.zoomStep = lookAroundZoomStep;
        }
      }, [lookAroundControls, lookAroundZoomStep]);
      useEffect(() => {
        if (navigationControls) {
          navigationControls.orbitControls.rotateSpeed =
            orbitRotationSpeed ?? defaulOrbitRotationSpeed;
        }
      }, [navigationControls, orbitRotationSpeed]);

      useEffect(() => {
        const helper = new DraggingHelper(domElement, camera, enableControls);
        setDraggingHelper(helper);
      }, [domElement, camera, enableControls, setDraggingHelper]);

      useEffect(() => {
        if (!pickHelper) return;
        if (currentImage?.imageType === ImageType.Spherical) {
          pickHelper.enableViewerSphere(currentImage.position, sphereRadius ?? 10);
        } else {
          pickHelper.disableViewerSphere();
        }
      }, [pickHelper, sphereRadius, currentImage]);

      useHybridRaycaster(pickCanvasAreaPercent ?? defaultPickCanvasAreaPercent, isSphericalMode);

      const onSelectionDone = useCallback(
        (selectBox: SelectBox, mouseEvent: PointerEvent) => {
          if (pickHelper !== undefined && camera !== undefined && gl !== undefined && selectBox) {
            const result = pickHelper.pickPoints(mouseEvent, selectBox, camera, gl);
            if (onPointCloudSelection) onPointCloudSelection(result);
          }
        },
        [pickHelper, camera, gl, onPointCloudSelection]
      );

      useSelectBoxControls(
        navigationControls,
        domElement,
        onSelectionDone,
        selectionColors,
        selectionBoxIsEnabled
      );

      const invalidateFrame = useCallback(() => {
        invalidate();
        regress();
      }, [invalidate, regress]);

      useEffect(() => {
        invalidate();
      });

      useEffect(() => {
        if (navigationControls) {
          setCameraMover(new CameraMover(navigationControls, invalidateFrame));
        }
      }, [navigationControls, invalidateFrame]);

      const handleSetOrbitControlsReference = useCallback(
        (orbitControls: OrbitControlsImpl) => {
          if (orbitControls) {
            const navControls = new NavigationControls(
              camera,
              domElement,
              orbitControls,
              invalidateFrame
            );
            setNavigationControls(navControls);
          }
        },
        [camera, domElement, invalidateFrame, setNavigationControls]
      );

      const edlRenderer = useEdlRenderer({
        gl,
        camera,
        scene,
        enableEDL,
        edlStrength,
        invalidateFrame: invalidate,
      });

      const vboxUniformsReference = useRef(makeVisibilityBoxUniforms());
      const visibilityBoxUniforms = vboxUniformsReference.current;

      const pointCloudMaterialMap = useMemo(() => {
        const scale = calcScale(
          gl.domElement.height,
          camera instanceof THREE.PerspectiveCamera ? camera.fov : 50
        );
        const materialMap = new Map<string, PointCloudMaterial>();
        if (edlRenderer) {
          pointCloudMaterials?.forEach((materialProps, name) => {
            materialMap.set(
              name,
              new PointCloudMaterial(
                { ...materialProps, invalidateFrame: invalidate },
                visibilityBoxUniforms,
                edlRenderer,
                scale
              )
            );
          });
        }
        return materialMap;
      }, [
        pointCloudMaterials,
        visibilityBoxUniforms,
        edlRenderer,
        gl.domElement,
        camera,
        invalidate,
      ]);

      const previousScale = useRef(0);

      useFrame(() => {
        const scale = calcScale(
          gl.domElement.height,
          camera instanceof THREE.PerspectiveCamera ? camera.fov : 50
        );
        if (previousScale.current !== scale) {
          pointCloudMaterialMap.forEach((material) => {
            material.setScale(scale);
          });
          previousScale.current = scale;
        }
      }, CALLBACK_ORDER_AFTER_CAMERA_UPDATE);

      useEffect(() => {
        return () => {
          pointCloudMaterialMap.forEach((material) => {
            material.dispose();
          });
        };
      }, [pointCloudMaterialMap]);

      useEdlEffect({ enableEDL, gl, scene, camera, edlRenderer, pointCloudMaterialMap });

      const getPointCloudMaterial = useCallback(
        (material: string): PointCloudMaterial | undefined => pointCloudMaterialMap.get(material),
        [pointCloudMaterialMap]
      );

      useEffect(() => {
        gl.localClippingEnabled = true;
      }, [gl]);

      const materialsReference = useRef(new Set<THREE.Material>());
      const materials = materialsReference.current;

      const addMaterial = useCallback(
        (material: THREE.Material, enableClippingPlanes: boolean) => {
          if (enableClippingPlanes) {
            material.clippingPlanes = clippingPlanes ?? null;
          }
          // material.clipIntersection = false;
          // material.clipShadows = true;
          // material.side = THREE.DoubleSide;
          material.needsUpdate = true;

          materials.add(material);
        },
        [clippingPlanes, materials]
      );

      const removeMaterial = useCallback(
        (material: THREE.Material) => {
          materials.delete(material);
        },
        [materials]
      );

      const {
        visibilityBox,
        setVisibilityBox,
        smallVisibilityBox,
        setSmallVisibilityBox,
        imageAnchorsVisibilityBox,
      } = useVisibilityBox({
        smallVisibilityBoxOnParent,
        setSmallVisibilityBoxOnParent,
        visibilityBoxOnParent,
        setVisibilityBoxOnParent,
        vboxUniformsReference,
        imageAnchorsExtraRange: sphericalImageVisibilityRange,
        pointCloudMaterialMap,
        materials,
      });

      const visibilityBoxMode = visibilityBox.mode;

      const savedFovUp = useRef<{ fov: number; up: Number3 } | undefined>(undefined);

      const enterSphericalViewMode = useCallback(
        (imageProps: CurrentImageProps) => {
          if (!navigationControls || !cameraMover) {
            return;
          }
          navigationControls.setControlType(NavigationControlType.Orbit);
          camera.layers.enable(ViewerLayer.Default);
          camera.layers.disable(ViewerLayer.SphericalViewerLayer);

          const {
            imageType,
            position: positionProps,
            rotation: rotationProps,
            lookAt: lookAtProps,
            fov: fovProps,
          } = imageProps;
          const position = new THREE.Vector3(...positionProps);

          if (!savedFovUp.current && camera instanceof THREE.PerspectiveCamera) {
            const { x, y, z } = camera.up;
            savedFovUp.current = { fov: camera.fov, up: [x, y, z] };
          }

          const quat = cameraQuaternionFromAbyssRotation(rotationProps);
          camera.up.set(0, 1, 0);
          camera.up.applyQuaternion(quat);
          navigationControls.lookAroundControls.setUpQuat(quat);

          let fov = fovProps;
          let lookAt: Vector3 | undefined;

          if (lookAtProps) {
            lookAt = new THREE.Vector3(lookAtProps.x, lookAtProps.y, lookAtProps.z);
          } else if (imageType === ImageType.Rectilinear) {
            lookAt = new THREE.Vector3(0, 0, -1);
            lookAt.applyQuaternion(quat);
            lookAt.add(position);

            if (currentImage?.imageType !== ImageType.Rectilinear) {
              const { height, focalLength } = imageProps;
              fov = 1.5 * fovFromCalibration(focalLength, height);
            }
          } else {
            // Spherical
            // preserve world direction when moving
            if (camera.position.distanceToSquared(position) > 0.5) {
              lookAt = new THREE.Vector3();
              camera.getWorldDirection(lookAt);
              lookAt.z = 0;
              lookAt.multiplyScalar(defaultTargetDistance);
              lookAt.add(position);
            } else {
              lookAt = undefined;
            }
          }

          cameraMover.moveCameraTo({
            position,
            lookAt,
            fov,
            transitionTime: 0.5,
            onComplete: () => {
              navigationControls.setControlType(NavigationControlType.LookAround);
              camera.layers.disable(ViewerLayer.Default);
              camera.layers.enable(ViewerLayer.SphericalViewerLayer);
              setCurrentImage(imageProps);
            },
          });
        },
        [navigationControls, camera, cameraMover, setCurrentImage, currentImage]
      );

      const exitSphericalViewMode = useCallback(() => {
        if (!navigationControls) {
          return;
        }
        camera.layers.enable(ViewerLayer.Default);
        camera.layers.disable(ViewerLayer.SphericalViewerLayer);

        navigationControls.setControlType(NavigationControlType.Orbit);

        if (savedFovUp.current && camera instanceof THREE.PerspectiveCamera && cameraMover) {
          const { fov, up } = savedFovUp.current;
          camera.up.set(...up);
          navigationControls.lookAroundControls.setUpQuat(new THREE.Quaternion());
          cameraMover.moveCameraTo({ fov });
        }
        savedFovUp.current = undefined;

        setCurrentImage(undefined);
      }, [camera, navigationControls, cameraMover, setCurrentImage]);

      const pickPointCloud = useCallback(
        (event: PointerEvent | MouseEvent): PointPickResult | undefined => {
          return pickHelper?.pick(
            event,
            camera,
            gl,
            pickCanvasAreaPercent ?? defaultPickCanvasAreaPercent
          );
        },
        [pickHelper, camera, gl, pickCanvasAreaPercent]
      );

      const previousSpherical = useRef<SphericalImageProps | undefined>(undefined);

      useEffect(() => {
        if (!navigationControls) return;
        if (!cameraMover) return;
        if (
          shouldHandleSphericalViewCameraTransitions !== undefined &&
          !shouldHandleSphericalViewCameraTransitions
        ) {
          return;
        }

        if (previousSpherical.current !== nextCurrentImage) {
          previousSpherical.current = nextCurrentImage;
          if (nextCurrentImage) {
            enterSphericalViewMode(nextCurrentImage);
          } else {
            exitSphericalViewMode();
          }
        }
      }, [
        navigationControls,
        cameraMover,
        enterSphericalViewMode,
        exitSphericalViewMode,
        nextCurrentImage,
        shouldHandleSphericalViewCameraTransitions,
      ]);

      const handleImageLoadError = useCallback(
        (image: CurrentImageProps, error: Error, info: React.ErrorInfo) => {
          if (onFileLoadError) {
            onFileLoadError(ViewerFileType.VIEWER_IMAGE, image.url, error, info);
          } else {
            throw Error(`Unable to load image: ${image.url}`);
          }
        },
        [onFileLoadError]
      );

      const allImages = useMemo<ImageProps[] | undefined>(
        () => imageSets?.flatMap((imageSet) => imageSet.images),
        [imageSets]
      );

      const sphericalImageViewer = useMemo(() => {
        switch (nextCurrentImage?.imageType) {
          case ImageType.Spherical:
            return (
              <LoadErrorBoundary
                onError={(error: Error, info: React.ErrorInfo) =>
                  handleImageLoadError(nextCurrentImage, error, info)
                }
              >
                <Suspense fallback={null}>
                  <SphericalImageViewer
                    {...nextCurrentImage}
                    onClick={onSphericalViewClick}
                    setNextCurrentImage={setNextCurrentImage}
                    opacity={sphericalViewerImageOpacity ?? 1}
                    sphereRadius={sphereRadius}
                    pickPointCloud={pickPointCloud}
                    viewerSphere={sharedGeometries?.viewerSphere}
                    brightness={imageBrightness}
                    contrast={imageContrast}
                  />
                </Suspense>
              </LoadErrorBoundary>
            );
          case ImageType.Rectilinear:
            return (
              <LoadErrorBoundary
                onError={(error: Error, info: React.ErrorInfo) =>
                  handleImageLoadError(nextCurrentImage, error, info)
                }
              >
                <Suspense fallback={null}>
                  <ImageViewer
                    image={nextCurrentImage}
                    onClick={onSphericalViewClick}
                    setNextCurrentImage={setNextCurrentImage}
                    opacity={sphericalViewerImageOpacity ?? 1}
                    pickPointCloud={pickPointCloud}
                    allImages={allImages}
                    showMaxNeighbouringImages={showMaxNeighbouringImages}
                    maxNeighbouringImageAngle={maxNeighbouringImageAngle}
                    brightness={imageBrightness}
                    contrast={imageContrast}
                  />
                </Suspense>
              </LoadErrorBoundary>
            );
          default:
            return undefined;
        }
      }, [
        nextCurrentImage,
        setNextCurrentImage,
        onSphericalViewClick,
        sphericalViewerImageOpacity,
        imageBrightness,
        imageContrast,
        sphereRadius,
        pickPointCloud,
        sharedGeometries,
        allImages,
        handleImageLoadError,
        showMaxNeighbouringImages,
        maxNeighbouringImageAngle,
      ]);

      const getDefaultCameraPosition = useCallback(
        (object3d: Object3D) => {
          const bbox = new Box3();
          bbox.setFromObject(object3d);

          const sphere = new THREE.Sphere();
          bbox.getBoundingSphere(sphere);

          const lookAt = sphere.center;
          const objectSize = 2 * sphere.radius;

          if (camera instanceof THREE.PerspectiveCamera) {
            const fov = defaultCameraFieldOfView;
            const distribution = objectSize / 2 / Math.tan((Math.PI * fov) / 180 / 2);
            // 45 degrees North/East X/Y
            // 45 degrees elevation Z
            const xyzDistribution = distribution / Math.sqrt(3);

            const position = new THREE.Vector3(-1, -1, 1)
              .multiplyScalar(xyzDistribution)
              .applyEuler(new THREE.Euler(0, 0, -1 * (northRotation || 0), 'XYZ'))
              .add(lookAt);

            return { lookAt, position, fov };
          }

          // Orthographic camera

          const orthographicCamera = camera as THREE.OrthographicCamera;
          const frustumSize = Math.min(
            orthographicCamera.right - orthographicCamera.left,
            orthographicCamera.top - orthographicCamera.bottom
          );

          const position = new THREE.Vector3(-1, -1, 1)
            .multiplyScalar(objectSize)
            .applyEuler(new THREE.Euler(0, 0, -1 * (northRotation || 0), 'XYZ'))
            .add(lookAt);

          const zoom = frustumSize / objectSize;
          return { lookAt, position, zoom };
        },
        [camera, northRotation]
      );

      const focusOnObject = useCallback(
        (object3d: Object3D) => {
          const defaultPosition = getDefaultCameraPosition(object3d);
          if (!defaultPosition || !camera) {
            return;
          }

          if (isSphericalMode) {
            return;
          }

          if (cameraMover) {
            cameraMover.moveCameraTo({ ...defaultPosition });
          }
        },
        [camera, cameraMover, getDefaultCameraPosition, isSphericalMode]
      );

      const moveCameraToViewTarget = useCallback(
        (lookAtProps: [x: number, y: number, z: number]) => {
          const lookAt = new THREE.Vector3(...lookAtProps);
          cameraMover?.moveCameraTo({ lookAt });
        },
        [cameraMover]
      );

      const moveCameraToViewDirection = useCallback(
        (direction: [x: number, y: number, z: number]) => {
          if (!cameraMover || !navigationControls) {
            return;
          }
          const worldDirection = new THREE.Vector3(...direction);
          const { position, lookAt } = navigationControls.calculateViewDirection(worldDirection);
          if (isSphericalMode) {
            cameraMover.moveCameraTo({ lookAt });
          } else {
            cameraMover.moveCameraTo({ position, lookAt });
          }
        },
        [cameraMover, navigationControls, isSphericalMode]
      );

      const [animationPlayer, setAnimationPlayer] = useState<AnimationPlayer | null>(null);

      const alignWithCameraUpAxis = useCallback(
        (transform: Matrix4): Matrix4 | undefined => {
          if (camera && navigationControls) {
            const target = navigationControls.getTarget();
            const { position } = camera;

            const direction = new THREE.Vector3();
            direction.copy(target).sub(position).normalize();

            const frontDirection = new THREE.Vector3(0, 1, 0);

            return alignModelByTwoVectors(direction, frontDirection, transform);
          }
          return undefined;
        },
        [camera, navigationControls]
      );

      const getSceneBoundingBox = useCallback(
        () => (allObjectsGroup ? getObjectBoundingBox(allObjectsGroup) : undefined),
        [allObjectsGroup]
      );

      const getBoundingBox = useCallback(
        (id: string, applyMatrix?: boolean) => {
          const target = getTransformTarget(id);
          if (!target) return undefined;
          return getTransformTargetBoundingBox(target, applyMatrix);
        },
        [getTransformTarget]
      );

      const brushToolHelperReference = useRef<BrushToolHelper>();

      useEffect(() => {
        brushToolHelperReference.current = new BrushToolHelper(
          domElement,
          camera,
          gl,
          enableControls,
          pickHelper
        );
        return () => {
          brushToolHelperReference.current?.dispose();
        };
      }, [domElement, camera, gl, enableControls, pickHelper]);

      const brushToolHelper = brushToolHelperReference.current;

      useEffect(() => {
        const { isEnabled, onBrushing, brushSize } = brushToolProps ?? {
          isEnabled: false,
          onBrushing: () => {},
        };
        if (isEnabled) {
          brushToolHelper?.activate(onBrushing, brushSize);
        } else {
          brushToolHelper?.deactivate();
        }
      }, [brushToolHelper, brushToolProps, brushToolProps?.isEnabled, brushToolProps?.onBrushing]);

      const findFocusPoint = useCallback(
        (cameraPosition: Number3, attributeName: string, attributeValues: number[]) => {
          return pickHelper?.findFocusPoint(
            cameraPosition,
            attributeName,
            attributeValues,
            camera,
            gl
          );
        },
        [pickHelper, camera, gl]
      );

      const getHTMLCanvas = useCallback(() => {
        return screenShotHelperReference?.current?.getHTMLCanvas();
      }, [screenShotHelperReference]);

      const takeScreenShot = useCallback(() => {
        return screenShotHelperReference?.current?.takeScreenShot();
      }, [screenShotHelperReference]);

      const getGPUInfo = useCallback(() => {
        const context = gl.getContext();
        const extension = context.getExtension('WEBGL_debug_renderer_info');
        if (extension) {
          const vendor = context.getParameter(extension?.UNMASKED_VENDOR_WEBGL);
          const renderer = context.getParameter(extension?.UNMASKED_RENDERER_WEBGL);
          return { vendor, renderer };
        }
        return undefined;
      }, [gl]);

      useImperativeHandle(
        selfReference,
        () => ({
          getCameraPosition: () => {
            if (navigationControls) {
              const lookAt = navigationControls.getTarget();
              const { position, rotation } = camera;
              return {
                lookAt: [lookAt.x, lookAt.y, lookAt.z] as [x: number, y: number, z: number],
                position: [position.x, position.y, position.z] as [x: number, y: number, z: number],
                rotation: [rotation.x, rotation.y, rotation.z],
                fov: camera instanceof THREE.PerspectiveCamera ? camera.fov : undefined,
                zoom: camera instanceof THREE.OrthographicCamera ? camera.zoom : undefined,
              };
            }
            return undefined;
          },
          alignWithCameraUpAxis,
          moveCameraToViewDirection,
          getDefaultCameraPosition: () => {
            if (allObjectsGroup === undefined) {
              return undefined;
            }
            const defaultPosition = getDefaultCameraPosition(allObjectsGroup);
            if (!defaultPosition) {
              return undefined;
            }
            const { lookAt, position } = defaultPosition;
            return {
              lookAt: [lookAt.x, lookAt.y, lookAt.z] as [x: number, y: number, z: number],
              position: [position.x, position.y, position.z] as [x: number, y: number, z: number],
            };
          },
          resetCameraPositionToDefault: () => {
            if (isSphericalMode) return;
            if (currentImage) {
              enterSphericalViewMode(currentImage);
              return;
            }
            if (allObjectsGroup !== undefined) {
              focusOnObject(allObjectsGroup);
            }
          },
          startAnimation: (animation: AnimationProps, onComplete: () => void = () => {}) => {
            if (animationPlayer) {
              animationPlayer.stop();
            }
            if (cameraMover) {
              setAnimationPlayer(
                startAnimationImpl({ animation, cameraMover, onComplete, setTimeoutManaged })
              );
            }
          },
          stopAnimation: () => {
            if (animationPlayer) {
              animationPlayer.stop();
              setAnimationPlayer(null);
            }
          },
          getSceneBoundingBox,
          getBoundingBox,
          getHTMLCanvas,
          getGPUInfo,
          takeScreenShot,
          getFrameTimings: () => ({ ...frameTimings.current }),
          findFocusPoint,
        }),
        [
          currentImage,
          isSphericalMode,
          allObjectsGroup,
          navigationControls,
          camera,
          cameraMover,
          animationPlayer,
          frameTimings,
          alignWithCameraUpAxis,
          enterSphericalViewMode,
          focusOnObject,
          getBoundingBox,
          getDefaultCameraPosition,
          getGPUInfo,
          getHTMLCanvas,
          getSceneBoundingBox,
          moveCameraToViewDirection,
          takeScreenShot,
          findFocusPoint,
          setTimeoutManaged,
        ]
      );

      const setCenterOfRotation = useCallback(
        (target: [x: number, y: number, z: number]) => {
          moveCameraToViewTarget(target);
        },
        [moveCameraToViewTarget]
      );

      const [allObjectsBbox, setAllObjectsBbox] = useState<THREE.Box3>();

      const [hasInitialViewSet, setHasInitialViewSet] = useState<boolean>(false);

      const waitReadyCount =
        (simplePointClouds ? simplePointClouds.length : 0) + (models ? models.length : 0);
      const readyCount = useRef<number>(0);

      const updateAllObjectsBbox = useCallback(() => {
        if (!allObjectsGroup) return;
        const bbox = new THREE.Box3();
        bbox.setFromObject(allObjectsGroup);
        setAllObjectsBbox(bbox);
      }, [allObjectsGroup]);

      const checkAndSetInitialView = useCallback(() => {
        if (
          hasInitialViewSet === false &&
          readyCount.current === waitReadyCount &&
          allObjectsGroup
        ) {
          setHasInitialViewSet(true);

          setTimeoutManaged(() => {
            updateAllObjectsBbox();
            if (onSceneLoaded !== undefined) {
              onSceneLoaded();
            } else if (cameraTarget === undefined) {
              focusOnObject(allObjectsGroup);
            }
          }, 1);
        }
      }, [
        cameraTarget,
        hasInitialViewSet,
        waitReadyCount,
        focusOnObject,
        onSceneLoaded,
        allObjectsGroup,
        setTimeoutManaged,
        updateAllObjectsBbox,
      ]);

      const onViewerObjectReady = useCallback(
        (isReady: boolean) => {
          if (isReady === true) {
            readyCount.current += 1;
            checkAndSetInitialView();
          } else {
            readyCount.current -= 1;
          }
        },
        [checkAndSetInitialView]
      );

      const isBrushToolActive = useCallback(() => {
        return Boolean(brushToolHelper?.isActive);
      }, [brushToolHelper]);

      const { onPointerMoveGlobal, onPointerUpGlobal, onPointerDownGlobal, onPointerOutGlobal } =
        useClickHandlers({
          camera,
          pickPointCloud,
          setCenterOfRotation,
          isBrushToolActive,
          onClick,
          onMeshClick,
          onPointCloudClick,
          onPointCloudHover,
        });

      const onMarkerClick = useCallback(
        (markerId: string, button: number) => {
          if (button === 0 && onAnnotationClick) {
            onAnnotationClick(markerId);
          }
        },
        [onAnnotationClick]
      );

      const pointCloudsWithId = useMemo(
        () => pointClouds?.map((pointCloud) => makePropsWithId(pointCloud)) || [],
        [pointClouds]
      );

      const handleInitialPointCloudLoad = useCallback(() => {
        setTimeoutManaged(() => {
          updateAllObjectsBbox();
          onInitialPointCloudLoad?.();
        }, 1);
      }, [setTimeoutManaged, updateAllObjectsBbox, onInitialPointCloudLoad]);

      const fileLoaderOptionsContext: FileLoaderOptions = useMemo(
        () => ({
          withCredentials: !!fileLoaderOptions?.withCredentials,
        }),
        [fileLoaderOptions]
      );

      // const oldReference = useRef(fileLoaderOptionsContext);

      // useEffect(() => {
      //   console.log(oldReference.current === fileLoaderOptionsContext);
      //   oldReference.current = fileLoaderOptionsContext;
      // });

      const polygonsComp = useMemo(
        () =>
          polygons?.map((polygon) => {
            const props = makePropsWithId(polygon);
            const { id, borderPoints } = props;
            return (
              // make sure id exist
              <Suspense fallback={null} key={`${id}-blister-suspense`}>
                <Polygon {...props} borderPoints={borderPoints} />
              </Suspense>
            );
          }),
        [polygons]
      );

      const floorLevelsBbox = useMemo(() => {
        if (visibilityBoxMode === VisibilityBoxMode.Disabled) {
          return allObjectsBbox;
        }
        return getBoundingBoxOfRotatedVBox(visibilityBox.matrix);
      }, [visibilityBoxMode, allObjectsBbox, visibilityBox]);

      const floorLevelsComp = useMemo(
        () =>
          floorLevelsBbox &&
          floorLevels?.map((floorLevelProps) => {
            const props = makePropsWithId(floorLevelProps);
            return <FloorLevel {...props} boundingBox={floorLevelsBbox} key={props.id} />;
          }),
        [floorLevels, floorLevelsBbox]
      );

      const pointLightsComp = useMemo(
        () =>
          pointLights.map((props: PointLightProps, index: number) => (
            <pointLight {...props} key={`point-light-${String(index)}`} />
          )),
        [pointLights]
      );

      const modelsComp = useMemo(
        () =>
          models?.map((modelProps) => {
            const props = makePropsWithId(modelProps);
            const { id } = props;
            return (
              <Suspense fallback={null} key={`${id}-suspense`}>
                <Model
                  {...makePropsWithId(modelProps)}
                  key={id}
                  onViewerObjectReady={onViewerObjectReady}
                  addMaterial={addMaterial}
                  removeMaterial={removeMaterial}
                />
              </Suspense>
            );
          }),
        [models, onViewerObjectReady, addMaterial, removeMaterial]
      );

      const markersComp = useMemo(
        () =>
          markers?.map((markerProps) => {
            const props = makePropsWithId(markerProps);
            const { id } = props;
            return <Marker {...props} onMarkerClick={onMarkerClick} key={id} />;
          }),
        [markers, onMarkerClick]
      );

      const svgMarkersComp = useMemo(
        () =>
          svgMarkers?.map((svgMarkerProps) => {
            const { position } = svgMarkerProps;

            return (
              svgMarkerProps && (
                <SvgMarker
                  {...svgMarkerProps}
                  key={`poiMarker${position[0]},${position[1]},${position[2]}`}
                />
              )
            );
          }),
        [svgMarkers]
      );

      const svgMarkerSetsComp = useMemo(
        () =>
          svgMarkerSets?.map((markerSet) => (
            <SvgMarkerSet {...markerSet} key={markerSet.id} visibilityBox={visibilityBox} />
          )),
        [svgMarkerSets, visibilityBox]
      );

      const htmlMarkerSetsComp = useMemo(
        () =>
          htmlMarkerSets?.map((markerSet) => (
            <HtmlMarkerSet {...markerSet} key={markerSet.id} visibilityBox={visibilityBox} />
          )),
        [htmlMarkerSets, visibilityBox]
      );

      const segmentsComp = useMemo(
        () =>
          segments?.map((segmentProps) => {
            const props = makePropsWithId(segmentProps);
            const { id } = props;
            return <Segment {...props} key={id} />;
          }),
        [segments]
      );

      const simplePointCloudsComp = useMemo(
        () =>
          simplePointClouds?.map((spcProps) => {
            const props = makePropsWithId(spcProps);
            const { id } = props;
            return (
              <Suspense fallback={null} key={`${id}-suspense`}>
                <SimplePointCloud
                  {...props}
                  getPointCloudMaterial={getPointCloudMaterial}
                  key={id}
                  onViewerObjectReady={onViewerObjectReady}
                  enableLoaderRetry={enableLoaderRetry}
                />
              </Suspense>
            );
          }),
        [simplePointClouds, getPointCloudMaterial, onViewerObjectReady, enableLoaderRetry]
      );

      const sphericalImageSetsComp = useMemo(
        () =>
          sphericalImageSets?.map((sphericalImageSetProps) => {
            const props = makePropsWithId(sphericalImageSetProps);
            const { id } = props;
            return (
              <InstancedSphericalImageSet
                {...props}
                anchorSphere={sharedGeometries.anchorSphere}
                isSphericalViewMode={currentImage !== undefined}
                visibilityBox={imageAnchorsVisibilityBox}
                key={id}
                currentImage={currentImage}
                setNextCurrentImage={setNextCurrentImage}
              />
            );
          }),
        [
          sphericalImageSets,
          sharedGeometries,
          currentImage,
          setNextCurrentImage,
          imageAnchorsVisibilityBox,
        ]
      );

      const sphericalImageSetsFromUrlComp = useMemo(
        () =>
          sphericalImageSetsFromUrl?.map((sphericalImageSetFromUrlProps) => {
            const props = makePropsWithId(sphericalImageSetFromUrlProps);
            const { id } = props;
            return (
              <Suspense fallback={null} key={`${id}-suspense`}>
                <SphericalImageSetFromUrl
                  {...props}
                  anchorSphere={sharedGeometries.anchorSphere}
                  isSphericalViewMode={currentImage !== undefined}
                  visibilityBox={imageAnchorsVisibilityBox}
                  key={id}
                  currentImage={currentImage}
                  setNextCurrentImage={setNextCurrentImage}
                />
              </Suspense>
            );
          }),
        [
          sphericalImageSetsFromUrl,
          sharedGeometries,
          currentImage,
          setNextCurrentImage,
          imageAnchorsVisibilityBox,
        ]
      );

      const imageSetsComp = useMemo(
        () =>
          imageSets?.map((imageSetProps: ImageSetProps) => {
            const props = makePropsWithId(imageSetProps);
            const { id } = props;
            return (
              <InstancedImageSet
                {...props}
                anchorSphere={sharedGeometries.anchorSphere}
                isImageViewMode={currentImage !== undefined}
                visibilityBox={imageAnchorsVisibilityBox}
                key={id}
                currentImage={currentImage}
                setNextCurrentImage={setNextCurrentImage}
              />
            );
          }),
        [imageSets, sharedGeometries, currentImage, setNextCurrentImage, imageAnchorsVisibilityBox]
      );

      const measurementsLinesMemo = useMemo(() => {
        return measurementLines && <MeasurementLines {...measurementLines} />;
      }, [measurementLines]);

      const visibilityBoxControlsMemo = useMemo(() => {
        const style = visibilityBox.style ?? defaultVisibilityBoxStyle;
        return (
          <>
            {visibilityBox.mode === VisibilityBoxMode.Adjusting && (
              <VisibilityBoxControl
                box={visibilityBox}
                step={visibilityBox.step}
                setBox={({ position, rotation, scale }: Transform) =>
                  setVisibilityBox({ ...visibilityBox, position, rotation, scale })
                }
                style={style}
              />
            )}
          </>
        );
      }, [visibilityBox, setVisibilityBox]);

      const smallVisibilityBoxControlsMemo = useMemo(() => {
        const style = smallVisibilityBox.style ?? defaultVisibilityBoxStyle;
        return (
          <>
            {smallVisibilityBox.mode === VisibilityBoxMode.Adjusting && (
              <VisibilityBoxControl
                box={smallVisibilityBox}
                step={smallVisibilityBox.step}
                setBox={({ position, rotation, scale }: Transform) =>
                  setSmallVisibilityBox({ ...smallVisibilityBox, position, rotation, scale })
                }
                style={style}
              />
            )}
          </>
        );
      }, [smallVisibilityBox, setSmallVisibilityBox]);

      return (
        <FileLoaderOptionsContext.Provider value={fileLoaderOptionsContext}>
          <group
            onPointerUp={onPointerUpGlobal}
            onPointerMove={onPointerMoveGlobal}
            onPointerDown={onPointerDownGlobal}
            onPointerOut={onPointerOutGlobal}
          >
            <MemoizedOrbitControls
              camera={camera}
              ref={handleSetOrbitControlsReference}
              enableDamping={false}
              domElement={domElement}
            />
            <ambientLight intensity={ambientLightIntensity} />
            <group>{pointLightsComp}</group>
            {viewCube ? (
              <ViewCube
                {...(viewCube === true ? defaultViewCubeProps : viewCube)}
                northRotation={northRotation || 0}
                moveCameraToViewDirection={moveCameraToViewDirection}
              />
            ) : undefined}
            {/* excluding floorLevels from allObjectsGroup bounding box */}
            <group>{floorLevelsComp}</group>
            <group>{measurementsLinesMemo}</group>
            {sphericalImageViewer}
            {visibilityBoxControlsMemo}
            {smallVisibilityBoxControlsMemo}
            <group ref={handleAllObjectsGroupReference}>
              <group ref={modelsGroup}>{modelsComp}</group>
              <group ref={markersGroup}>
                {markersComp}
                {svgMarkersComp}
                {svgMarkerSetsComp}
                {htmlMarkerSetsComp}
              </group>
              <group>{segmentsComp}</group>
              <group ref={simplePointCloudsGroup}>{simplePointCloudsComp}</group>
              <OctreePointCloudMaster
                pointClouds={pointCloudsWithId}
                getPointCloudMaterial={getPointCloudMaterial}
                showInSphericalView={
                  showPointCloudsInSphericalView == null ? false : showPointCloudsInSphericalView
                }
                onInitialPointCloudLoad={handleInitialPointCloudLoad}
                visibilityBox={visibilityBox}
                pointBudget={pointBudget}
                invisiblePointsBudgetRatio={invisiblePointsBudgetRatio}
                enableAdaptivePointBudget={enableAdaptivePointBudget}
                enableLoaderRetry={enableLoaderRetry}
                onOctreeProgress={onOctreeProgress}
                frameTimings={frameTimings}
                onFileLoadError={onFileLoadError}
              />
              <group ref={sphericalImageSetsGroup}>
                {sphericalImageSetsComp}
                {sphericalImageSetsFromUrlComp}
              </group>
              <group ref={imageSetsGroup}>{imageSetsComp}</group>
              {polygonsComp}
              {children}
            </group>
            {/* <axesHelper /> */}
          </group>
        </FileLoaderOptionsContext.Provider>
      );
    }
  )
);
AbyssViewerInner.displayName = 'AbyssViewerInner';

export const AbyssViewerNoContext = memo(
  forwardRef<AbyssViewerCallbacks, AbyssViewerNoContextProps>(
    ({ abyssViewerComponentProps, showStats, children, overlayNode }, selfReference) => {
      const { callbacks, setCallbacks, setOverlayNode } = useAbyssViewerContext();

      const abyssComponentReference = useRef<AbyssViewerCallbacks | null>(null);
      const screenShotHelperReference = useRef<ScreenShotHelperHandlers>(null);

      useEffect(() => {
        setOverlayNode(overlayNode);
      }, [setOverlayNode, overlayNode]);

      useImperativeHandle(selfReference, () => {
        return callbacks ?? {};
      }, [callbacks]);

      const referenceCB = useCallback(
        (reference: AbyssViewerCallbacks) => {
          setCallbacks(reference);
          abyssComponentReference.current = reference;
        },
        [setCallbacks]
      );

      return (
        <>
          <AdaptiveDpr pixelated />
          <AdaptiveEvents />
          <AbyssViewerInner
            abyssViewerComponentProps={abyssViewerComponentProps}
            screenShotHelperReference={screenShotHelperReference}
            ref={referenceCB}
          >
            {children}
          </AbyssViewerInner>
          <ScreenShotHelper ref={screenShotHelperReference} />
          {showStats === true ? <Stats showPanel={1} className="statsBox" /> : <></>}
        </>
      );
    }
  )
);
AbyssViewerNoContext.displayName = 'AbyssViewerNoContext';

export const AbyssViewer = memo(
  forwardRef<AbyssViewerCallbacks, AbyssViewerProps>(({ children, ...props }, selfReference) => {
    const abyssComponentReference = useRef<AbyssViewerCallbacks | null>(null);

    const [callbackState, setCallbackState] = useState<AbyssViewerCallbacks | undefined>(undefined);

    useImperativeHandle(selfReference, () => {
      return callbackState ?? {};
    }, [callbackState]);

    const referenceCB = useCallback((reference: AbyssViewerCallbacks) => {
      setCallbackState(reference);
      abyssComponentReference.current = reference;
    }, []);

    return (
      <AbyssViewerContextProvider {...props}>
        <AbyssViewerNoContext {...props} ref={referenceCB}>
          {children}
        </AbyssViewerNoContext>
      </AbyssViewerContextProvider>
    );
  })
);
AbyssViewer.displayName = 'AbyssViewer';
